import { useCallback, useEffect, useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  GET_ALL_NOTIFICATIONS,
  GET_NOTIFICATION_COUNT,
  type GetAllNotificationsData,
  type GetAllNotificationsVariables,
} from '@jwp/ott-common/src/graphql/query/notification.queries';
import {
  CLEAR_ALL_NOTIFICATIONS,
  TOGGLE_NOTIFICATIONS_READ_STATUS,
  type ClearAllNotificationsData,
  type ToggleNotificationsReadStatusData,
  type ToggleNotificationsReadStatusVariables,
} from '@jwp/ott-common/src/graphql/mutation/notification.mutations';
import { getAuthApolloClient } from '@jwp/ott-common/src/graphql/ApolloClient';
import { mapApiNotification } from '@jwp/ott-common/src/utils/mapNotification';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { GetCookie } from '@jwp/ott-common/src/utils/cookie';
import type { UserNotification } from '@jwp/ott-common/types/notification';

import { NOTIFICATIONS_PAGE_SIZE, UNREAD_NOTIFICATION_COUNT_FILTER } from '@jwp/ott-common/src/constants/notifications';

export { NOTIFICATIONS_PAGE_SIZE };

const GET_ALL_NOTIFICATIONS_QUERY_VARS = (limit: number, offset = 0): GetAllNotificationsVariables => ({
  limit,
  offset,
});

const isNotificationsNullError = (error: Error | undefined) =>
  !!error?.message?.includes('getAllNotifications');

type Options = {
  limit?: number;
  skip?: boolean;
};

export default function useUserNotifications({ limit = NOTIFICATIONS_PAGE_SIZE, skip = false }: Options = {}) {
  const user = useAccountStore((s) => s.user);
  const accountLoading = useAccountStore((s) => s.loading);
  const accessToken = GetCookie('AccessToken');
  const isEnabled = !skip && !!user && !accountLoading && !!accessToken;

  const notificationsClient = useMemo(() => getAuthApolloClient(), []);
  const queryVariables = useMemo(() => GET_ALL_NOTIFICATIONS_QUERY_VARS(limit, 0), [limit]);

  const { data, loading, error, fetchMore, refetch } = useQuery<GetAllNotificationsData, GetAllNotificationsVariables>(
    GET_ALL_NOTIFICATIONS,
    {
      client: notificationsClient,
      variables: queryVariables,
      skip: !isEnabled,
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
    },
  );

  const [toggleReadStatus] = useMutation<ToggleNotificationsReadStatusData, ToggleNotificationsReadStatusVariables>(
    TOGGLE_NOTIFICATIONS_READ_STATUS,
    { client: notificationsClient },
  );

  const [clearAllNotifications, { loading: clearingAll }] = useMutation<ClearAllNotificationsData>(CLEAR_ALL_NOTIFICATIONS, {
    client: notificationsClient,
    refetchQueries: [
      { query: GET_ALL_NOTIFICATIONS, variables: queryVariables },
      { query: GET_NOTIFICATION_COUNT, variables: { nIsRead: UNREAD_NOTIFICATION_COUNT_FILTER } },
    ],
    awaitRefetchQueries: true,
  });

  const notifications = useMemo<UserNotification[]>(
    () => (data?.getAllNotifications?.notifications ?? []).map(mapApiNotification),
    [data],
  );

  const total = data?.getAllNotifications?.total ?? 0;
  const hasMore = notifications.length < total;
  const fetchFailed = !!error && !data?.getAllNotifications;

  // Keep bell dot in sync with isAnyMessageUnRead while on the notifications page.
  useEffect(() => {
    const isAnyMessageUnRead = data?.getAllNotifications?.isAnyMessageUnRead;
    if (isAnyMessageUnRead === undefined) { return; }

    if (!isAnyMessageUnRead) {
      notificationsClient.cache.writeQuery({
        query: GET_NOTIFICATION_COUNT,
        variables: { nIsRead: UNREAD_NOTIFICATION_COUNT_FILTER },
        data: { getNotificationCount: 0 },
      });
      return;
    }

    void notificationsClient.refetchQueries({
      include: [{ query: GET_NOTIFICATION_COUNT, variables: { nIsRead: UNREAD_NOTIFICATION_COUNT_FILTER } }],
    });
  }, [data?.getAllNotifications?.isAnyMessageUnRead, notificationsClient]);

  const markAsRead = useCallback(
    async (notificationId: string) => {
      const notification = notifications.find((n) => n.id === notificationId);
      if (!notification || notification.isRead) { return; }

      await toggleReadStatus({
        variables: { input: { notificationIds: [notificationId] } },
        refetchQueries: [{ query: GET_NOTIFICATION_COUNT, variables: { nIsRead: UNREAD_NOTIFICATION_COUNT_FILTER } }],
        awaitRefetchQueries: true,
        update: (cache) => {
          const existing = cache.readQuery<GetAllNotificationsData, GetAllNotificationsVariables>({
            query: GET_ALL_NOTIFICATIONS,
            variables: queryVariables,
          });

          if (!existing?.getAllNotifications) { return; }

          const updatedNotifications = existing.getAllNotifications.notifications.map((n) =>
            n.nId === notificationId ? { ...n, nIsRead: true } : n,
          );

          cache.writeQuery<GetAllNotificationsData, GetAllNotificationsVariables>({
            query: GET_ALL_NOTIFICATIONS,
            variables: queryVariables,
            data: {
              getAllNotifications: {
                ...existing.getAllNotifications,
                notifications: updatedNotifications,
              },
            },
          });
        },
      });

      await notificationsClient.query({
        query: GET_ALL_NOTIFICATIONS,
        variables: queryVariables,
        fetchPolicy: 'network-only',
      });
    },
    [notifications, notificationsClient, queryVariables, toggleReadStatus],
  );

  const clearAll = useCallback(async () => {
    await clearAllNotifications();
  }, [clearAllNotifications]);

  const loadMore = useCallback(async () => {
    if (!hasMore || loading) { return; }

    await fetchMore({
      variables: GET_ALL_NOTIFICATIONS_QUERY_VARS(limit, notifications.length),
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult?.getAllNotifications) { return prev; }

        return {
          getAllNotifications: {
            ...fetchMoreResult.getAllNotifications,
            notifications: [
              ...(prev.getAllNotifications?.notifications ?? []),
              ...fetchMoreResult.getAllNotifications.notifications,
            ],
          },
        };
      },
    });
  }, [fetchMore, hasMore, limit, loading, notifications.length]);

  return {
    notifications,
    loading: isEnabled && loading && notifications.length === 0,
    loadingMore: isEnabled && loading && notifications.length > 0,
    error: fetchFailed && !isNotificationsNullError(error) ? error : undefined,
    fetchFailed,
    hasMore,
    total,
    markAsRead,
    clearAll,
    clearingAll,
    loadMore,
    refetch,
  };
}
